import FileViewerContainer from 'pages/fileViewer/FileViewerContainer';
import { Dialog, Slide } from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import MDTypography from 'components/MDTypography';
import app from 'config/app.json';
import { Preview } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { updateModalOpen } from '../../slice/fileViewerSlice';
import { getEntityParametersAsObject } from '../../utils/entityUtils';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

/**
 * FileViewerModal is a component that presents a file viewer in a full screen modal dialog.
 * It uses a slide-up transition when opening.
 *
 * @component
 * @example
 * <FileViewerModal />
 *
 * @returns {JSX.Element} The FileViewerModal component, which encapsulates a FileViewerContainer component
 * in a full screen modal dialog with slide-up transition.
 */
function FileViewerModal() {
  const { formatMessage: msg } = useIntl();
  const modalOpen = useSelector((state) => state.fileViewer.modalOpen);
  const dispatch = useDispatch();
  const previousHash = window.location.hash;
  const [modalTitle, setModalTitle] = useState('');

  const handleEntityUpdated = (entity) => {
    setModalTitle(entity ? buildModalTitle(entity) : '');
  };
  const handleDialogClose = () => {
    dispatch(updateModalOpen(false));
  };

  useEffect(() => {
    const handleHashChange = () => {
      dispatch(updateModalOpen(false));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [dispatch, previousHash]);

  return (
    <Dialog
      fullScreen
      open={modalOpen}
      onClose={handleDialogClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }} color="primary">
        <Toolbar variant="dense">
          <Preview color="white" fontSize="large" />
          <MDTypography sx={{ ml: 2, flex: 1 }} variant="h6" color="white" verticalAlign="middle">
            {modalTitle}
          </MDTypography>
          <Tooltip title={msg({ id: 'action.close' })}>
            <MDButton
              variant="gradient"
              color="primary"
              size="medium"
              onClick={handleDialogClose}
              sx={{ minWidth: '40px', padding: '0.3rem 0.75rem' }}
            >
              <CloseIcon fontSize="large" sx={{ width: '22px', height: '22px' }} />
            </MDButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <FileViewerContainer onEntityUpdated={handleEntityUpdated} />
    </Dialog>
  );

  function getEntitiesWithParents(entity) {
    return entity ? [entity, ...getEntitiesWithParents(entity.parent)] : [];
  }

  function buildModalTitle(entity) {
    try {
      return msg(
        {
          id: `view.fileViewer.${entity.entityTypeCode}.title`,
          defaultMessage: msg({ id: 'page.fileViewer' })
        },
        getEntityParametersAsObject(getEntitiesWithParents(entity), app.format.date.display)
      );
    } catch (error) {
      console.warn(error);
      return entity.name ?? msg({ id: 'page.fileViewer' });
    }
  }
}

export default FileViewerModal;
